// src/hooks/results/mapMeasurementRow.js
import { pickHrSignalQuality } from "../../components/results/views/quality";
import {
  parseTimestampFromRecordingId,
  formatDateParts,
} from "../../utils/results/recordingIdTime";

/**
 * Canonical HR accessor after backend changes.
 * Prefer heartdx.hr / heartdx.hr_median; fall back to legacy fields.
 */
export function readHrBpm(data) {
  const hd = data?.heartdx;

  const candidates = [data?.results?.heartdx?.data?.hr];

  for (const v of candidates) {
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return null;
}

/**
 * RR accessor (your existing tidal breathing fields).
 */
export function readRrBpm(data) {
  const rrRaw = data?.results?.mpm?.rr_avg ?? null;

  return typeof rrRaw === "number" && Number.isFinite(rrRaw) ? rrRaw : null;
}

export function readSignalQuality(data, context) {
  // Respiratory: from `results` (pass/fail)
  if (context === "respiratory") {
    const sqRaw = data?.results?.mpm?.quality ?? null;

    return typeof sqRaw === "string" ? sqRaw.trim().toLowerCase() : null;
  }

  // Pulse: prefer your existing quality fields if present
  // (these were used in PPG results, keep them as-is)
  return (
    data?.signal_quality_text ||
    data?.signalQualityText ||
    data?.ppg_signal_quality ||
    data?.heartdx?.ppg_signal_quality ||
    null
  );
}

export function readRrSignalQuality(data) {
  const sqRaw = data?.results?.mpm?.quality ?? null;

  const s = typeof sqRaw === "string" ? sqRaw.trim().toLowerCase() : null;
  return s === "pass" || s === "fail" ? s : null;
}

export function readHrSignalQuality(data) {
  // Reuse the same logic as the pulse scan result view
  return pickHrSignalQuality(data); // "very good" | "good" | "bad" | null
}

/**
 * Map a Firestore doc => UI row.
 * context: "pulse" | "respiratory"
 */
export function mapMeasurementDocToRow(
  doc,
  context,
  languageFallback = "en-GB",
) {
  const data = doc.data() || {};
  const ts = parseTimestampFromRecordingId(doc.id);
  const { time, date } = formatDateParts(
    ts,
    data?.language || languageFallback,
  );

  const hr = readHrBpm(data);
  const rr = readRrBpm(data);

  // Per-metric quality (independent)
  const rrQuality = readRrSignalQuality(data); // "pass" | "fail" | null
  const hrQuality = readHrSignalQuality(data); // "very good" | "good" | "bad" | null

  return {
    id: doc.id,
    timestamp: ts ? ts.toISOString() : null,
    time,
    date,

    hr: hr != null ? Math.round(hr) : null,
    rr,

    // NEW: expose quality per metric
    rrQuality,
    hrQuality,

    // keep old fields for anything still using them
    context,
    signalQuality: readSignalQuality(data, context),
    signalQualityCode: data?.signal_quality_code ?? null,

    raw: data,
  };
}
